import { documentToPlainTextString } from '@contentful/rich-text-plain-text-renderer'
import type { GetStaticPaths } from 'next'
import { paths } from '@/constants'
import { Durations } from '@/constants/durations'
import { publisher, releasedEvent, twitter } from '@/constants/seo'
import { BlogPostContextProvider } from '@/contexts/BlogPostContext'
import { Seo } from '@/layout/Seo'
import { ArticleJsonLd } from '@/layout/Seo/JsonLd'
import { BlogLayout, NextPageWithLayout } from '@/layouts'
import { BlogPost, extractCloudinaryImage, getPost, getRecentPosts } from '@/services/BlogService'
import { ProjectDetails, getProjectDetails } from '@/services/ProjectsService'
import { getCloudinaryImageUrl } from '@/utils/Cloudinary'
import { getStaticPropsErrorHandler } from '@/utils/nextUtils/nextErrorHandlers'
import { getBasePath } from '@/utils/sitemap/base'
import { loadTranslations } from '@/utils/translate/translate-server'
import BlogPostView from '@/views/BlogPostView'

interface BlogPostProps {
  locale: string
  post: BlogPost
  preview: boolean
  project: ProjectDetails
  recentPosts: BlogPost[]
}

const PostPage: NextPageWithLayout<BlogPostProps> = ({ locale, post, preview, project, recentPosts }) => {
  const cloudinaryImg = extractCloudinaryImage(post)
  const url = `${getBasePath(locale)}${paths.blog.index}/${project.slug}/posts/${post.slug}`
  const title = `${post.title} | Angel Studios`
  const openGraph = {
    title,
    description: post.excerpt,
    url,
    type: 'article',
    article: {
      publishedTime: post.sys.firstPublishedAt,
      modifiedTime: post.sys.publishedAt,
      section: post.section,
      authors: [twitter],
      tags: post.tags,
    },
    images: [
      {
        alt: post.header,
        height: 630,
        url: getCloudinaryImageUrl({
          height: 630,
          path: `v${cloudinaryImg?.version}/${cloudinaryImg?.public_id}.webp`,
          width: 1200,
          transforms: post.cloudinaryImageSocialSharingTransformation || 'w_1200,h_630,c_fill,g_faces',
        }),
        width: 1200,
      },
    ],
  }
  return (
    <>
      <ArticleJsonLd
        articleBody={documentToPlainTextString(post.content.json)}
        dateModified={post.sys.publishedAt}
        datePublished={post.sys.firstPublishedAt}
        description={post.excerpt}
        headline={post.title}
        images={[cloudinaryImg?.secure_url]}
        publication={releasedEvent}
        publisher={publisher}
        url={url}
      />
      <Seo canonical={url} openGraph={openGraph} title={title} description={post.excerpt} />
      <BlogPostContextProvider {...project}>
        <BlogPostView post={post} preview={preview} project={project} recentPosts={recentPosts} />
      </BlogPostContextProvider>
    </>
  )
}

PostPage.getLayout = (page) => {
  return <BlogLayout isDarkMode={false}>{page}</BlogLayout>
}

export const getStaticProps = getStaticPropsErrorHandler(
  { filePath: 'pages/[locale]/blog/[project]/posts/[slug].tsx' },
  async ({ federationClient, locale, next404, params, preview }) => {
    const post = await getPost({ preview, slug: params?.slug })
    const recentPosts = await getRecentPosts({ preview, project: params?.project, slug: params?.slug })
    const details = await getProjectDetails({ slug: params?.project as string }, federationClient)

    if (!post) {
      return next404(Durations.FIVE_MINUTES_IN_SECONDS)
    }

    return {
      props: {
        locale,
        recentPosts: recentPosts ?? [],
        post,
        preview,
        project: {
          name: details?.name || 'Angel Studios',
          pifEnabled: details?.pifEnabled ?? false,
          projectType: details?.projectType ?? 'movie',
          slug: details?.slug || (params?.project as string),
        },
        ...(await loadTranslations(locale, ['common', 'app-promo', 'account', 'guild', 'pif'])),
      },
      revalidate: Durations.ONE_HOUR_IN_SECONDS,
    }
  },
)

export const getStaticPaths: GetStaticPaths = () => {
  return {
    paths: [],
    fallback: 'blocking',
  }
}

export default PostPage
